import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { environment } from '../environments/environment';
import { IdleMonitorService } from './core/services/idle-monitor.service';
import { I18nService } from './core/i18n/i18n.service';
import { VoiceCallOverlayComponent } from './shared/components/voice-call-overlay/voice-call-overlay.component';
import { VoiceCallService } from './core/services/voice-call.service';
import { CampaignAttributionService } from './core/services/campaign-attribution.service';
import { SeoService } from './core/services/seo.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, VoiceCallOverlayComponent],
  template: `
    <router-outlet></router-outlet>
    <ng-container *ngIf="voiceCallService.call$ | async as snap">
      <app-voice-call-overlay
        [state]="snap.state"
        [callerName]="snap.peerName"
        [callerPhotoUrl]="snap.peerPhoto"
        [isMuted]="snap.isMuted"
        [isSpeakerOn]="snap.isSpeakerOn"
        (onAccept)="voiceCallService.acceptCall()"
        (onDecline)="voiceCallService.declineCall()"
        (onCancel)="voiceCallService.cancelCall()"
        (onEndCall)="voiceCallService.endCall()"
        (onMuteToggle)="voiceCallService.setMuted($event)"
        (onSpeakerToggle)="voiceCallService.setSpeaker($event)"
      ></app-voice-call-overlay>
    </ng-container>
  `,
  styles: []
})
export class AppComponent implements OnInit {
  title = 'SPREADLEE';
  environment = environment; // Make environment available in template

  constructor(
    private idleMonitor: IdleMonitorService,
    private i18n: I18nService,
    public voiceCallService: VoiceCallService,
    private attribution: CampaignAttributionService,
    private seo: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    // Robots/title tags per route: public pages index, signed-in and auth pages don't.
    this.seo.watchRouter();
    // Instantiating I18nService here ensures language + dir are initialized immediately.
    // Add global test methods for debugging.
    // Guarded: this constructor also runs during prerendering, where `window`
    // does not exist. Behaviour in the browser is unchanged.
    if (isPlatformBrowser(this.platformId)) {
      (window as any).testIdleDialog = () => this.idleMonitor.testIdleDialog();
      (window as any).resetIdleTimer = () => this.idleMonitor.resetIdleTimerPublic();
      (window as any).checkIdleStatus = () => {
        console.log('Idle status:', this.idleMonitor.isUserIdle());
      };
    }
  }

  ngOnInit() {
    // Remember which campaign brought this visitor, before they navigate away
    // from the landing URL.
    this.attribution.init();

    // ✅ ADDED: Log build information for deployment tracking
    //this.logBuildInfo();
    // ✅ ADDED: Prevent Chrome "Changes that you made may not be saved" modal
    this.preventBeforeUnloadWarning();

  }

  // ✅ ADDED: Log build information for deployment tracking
  private logBuildInfo() {
    if (environment.buildInfo) {
      console.log('🎯 Spreadlee App Initialized:', {
        title: this.title,
        version: environment.buildInfo.version,
        buildDate: environment.buildInfo.buildDate,
        buildId: environment.buildInfo.buildId,
        deployment: environment.buildInfo.deployment,
        features: environment.buildInfo.features,
        production: environment.production
      });
    } else {
      console.log('🎯 Spreadlee App Initialized (Development Mode)');
    }
  }

  // ✅ ADDED: Prevent Chrome "Changes that you made may not be saved" modal
  private preventBeforeUnloadWarning(): void {
    // No `window` and nothing to unload while prerendering.
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      // Simple approach: just add our own beforeunload listener that prevents warnings
      window.addEventListener('beforeunload', (event) => {
        // Prevent the default warning dialog
        event.preventDefault();
        // Clear the returnValue to prevent any warning
        event.returnValue = '';
        // Return undefined to prevent the warning
        return undefined;
      });

      // Also handle pagehide event to prevent any navigation warnings
      window.addEventListener('pagehide', (event) => {
        // This event is fired when the page is being unloaded
        // We don't want to show any warnings during navigation
        event.preventDefault();
      });

      // Handle unload event as well
      window.addEventListener('unload', (event) => {
        // Prevent any warnings during unload
        event.preventDefault();
      });

      // Additional safeguard: Clear any existing returnValue
      if (window.onbeforeunload) {
        window.onbeforeunload = null;
      }

      console.log('🔒 App: Beforeunload warnings disabled for SPA navigation');
    } catch (error) {
      console.warn('🔒 App: Could not disable beforeunload warnings:', error);
    }
  }

}
